import { Anchor, Box, Group, Text, type BoxProps } from "@mantine/core";
import {
  IconExternalLink,
  IconStarFilled,
} from "@tabler/icons-react";

import { GOOGLE_BUSINESS_REVIEWS } from "~/constants";
import { useTranslation } from "~/hooks/use-typed-translation";

interface BusinessReviewBadgeProps {
  align?: "left" | "center";
  mt?: BoxProps["mt"];
}

const GoogleG = () => (
  <Text
    component="span"
    fw={800}
    fz={18}
    lh={1}
    aria-hidden="true"
    style={{
      background:
        "conic-gradient(from -45deg, #4285f4 0 25%, #34a853 0 45%, #fbbc05 0 65%, #ea4335 0 82%, #4285f4 0)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      fontFamily: "Arial, Helvetica, sans-serif",
    }}>
    G
  </Text>
);

export const BusinessReviewBadge = ({
  align = "center",
  mt,
}: BusinessReviewBadgeProps) => {
  const { t } = useTranslation();
  const { rating, reviewCountLabel, reviewsUrl } = GOOGLE_BUSINESS_REVIEWS;
  const hasRating = typeof rating === "number";
  const roundedRating = Math.round(rating ?? 0);
  const justify = align === "center" ? "center" : "flex-start";

  return (
    <Box mt={mt}>
      <Group justify={justify} gap="xs" style={{ rowGap: "0.4rem" }}>
        <Group gap={6} wrap="nowrap">
          <GoogleG />
          <Text fz={{ base: 13, sm: 14 }} c="dimmed" fw={500}>
            {hasRating
              ? t("google-business-review-summary", {
                  rating: rating.toFixed(1),
                  count: reviewCountLabel,
                })
              : t("google-business-review-count", {
                  count: reviewCountLabel,
                })}
          </Text>
        </Group>

        {hasRating ? (
          <Group gap={3} wrap="nowrap" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <IconStarFilled
                key={index}
                size={14}
                color={
                  index < roundedRating
                    ? "var(--mantine-color-yellow-5)"
                    : "var(--mantine-color-gray-3)"
                }
              />
            ))}
          </Group>
        ) : null}

        <Anchor
          href={reviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          fz={{ base: 13, sm: 14 }}
          fw={600}>
          <Group gap={4} wrap="nowrap">
            {t("read-google-reviews")}
            <IconExternalLink size={14} />
          </Group>
        </Anchor>
      </Group>
    </Box>
  );
};
